import { useState } from 'react';
import { Link } from 'react-router-dom';
import { qs } from '../lib/api.js';
import { useResource } from '../lib/useApi.js';
import type { Deployment, DeploymentStatus } from '../lib/types.js';
import {
  Card,
  Empty,
  ErrorState,
  Loading,
  Pill,
  StatusPill,
  When,
} from '../components/ui.js';

const STATUSES: Array<DeploymentStatus | ''> = [
  '',
  'SCHEDULED',
  'RUNNING',
  'SUCCEEDED',
  'PARTIAL',
  'FAILED',
  'CANCELLED',
];

/** Deployment history. Polls while anything is running, then stops. */
export function DeploymentsPage(): JSX.Element {
  const [status, setStatus] = useState<DeploymentStatus | ''>('');

  const list = useResource<{ deployments: Deployment[]; total: number; runningId: string | null }>(
    `/api/deployments${qs({ status, take: 100 })}`,
    { pollMs: 5000 },
  );

  const active = list.data?.deployments.filter((d) =>
    ['RUNNING', 'QUEUED', 'SCHEDULED'].includes(d.status),
  );

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Deployments</h1>
          <p className="sub">
            Every push, scheduled run and rollback, with a per-user record of who received what and
            when.
          </p>
        </div>
        <div className="page-actions">
          <Link className="btn primary" to="/deploy">
            New deployment
          </Link>
        </div>
      </header>

      <div className="page-body">
        {list.error && <ErrorState error={list.error} onRetry={list.reload} />}

        <div className="toolbar">
          <div className="seg">
            {STATUSES.map((s) => (
              <button key={s || 'all'} className={status === s ? 'on' : ''} onClick={() => setStatus(s)}>
                {s === '' ? 'All' : s.toLowerCase()}
              </button>
            ))}
          </div>
          <span className="spacer" />
          {active && active.length > 0 && (
            <Pill tone="accent" dot pulse>
              {active.length} in progress
            </Pill>
          )}
          <span className="tiny muted">{list.data?.total ?? 0} total</span>
        </div>

        {list.loading && !list.data && <Loading rows={4} label="Loading deployments" />}

        {list.data && list.data.deployments.length === 0 && (
          <Card>
            <Empty
              title={status ? `No ${status.toLowerCase()} deployments` : 'No deployments yet'}
              action={
                <Link className="btn primary" to="/deploy">
                  Start one
                </Link>
              }
            >
              A deployment records what was pushed, to whom, and what happened to each mailbox.
            </Empty>
          </Card>
        )}

        {list.data && list.data.deployments.length > 0 && (
          <Card tight>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Template</th>
                    <th>Target</th>
                    <th>Mechanism</th>
                    <th>Outcome</th>
                    <th>Started</th>
                    <th>By</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.data.deployments.map((d) => (
                    <tr key={d.id}>
                      <td className="nowrap">
                        <StatusPill status={d.status} />
                        {d.kind !== 'PUSH' && <Pill tone="outline"> {d.kind.toLowerCase()}</Pill>}
                        {d.dryRun && <Pill tone="accent"> dry run</Pill>}
                      </td>
                      <td>
                        {d.template ? (
                          <Link to={`/templates/${d.template.id}`}>{d.template.name}</Link>
                        ) : (
                          <span className="muted">—</span>
                        )}
                        {d.version && <span className="tiny muted"> v{d.version.version}</span>}
                      </td>
                      <td className="tiny">{d.targetLabel ?? d.targetType}</td>
                      <td className="tiny mono">{d.providerKey}</td>
                      <td style={{ minWidth: 150 }}>
                        <OutcomeBar deployment={d} />
                      </td>
                      <td className="tiny muted nowrap">
                        {d.status === 'SCHEDULED' ? (
                          <>
                            scheduled <When iso={d.scheduledFor} />
                          </>
                        ) : (
                          <When iso={d.startedAt ?? d.createdAt} />
                        )}
                      </td>
                      <td className="tiny">{d.createdBy?.displayName ?? '—'}</td>
                      <td className="right">
                        <Link className="btn sm" to={`/deployments/${d.id}`}>
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

/** Success / failure / skipped proportions, so a partial run reads at a glance. */
export function OutcomeBar({ deployment }: { deployment: Deployment }): JSX.Element {
  const { totalCount, succeededCount, failedCount, skippedCount } = deployment;
  if (totalCount === 0) return <span className="muted tiny">—</span>;

  const pct = (n: number) => `${(n / totalCount) * 100}%`;

  return (
    <div>
      <div className="bar" role="img" aria-label={`${succeededCount} succeeded, ${failedCount} failed, ${skippedCount} skipped`}>
        <span className="ok" style={{ width: pct(succeededCount) }} />
        <span className="fail" style={{ width: pct(failedCount) }} />
        <span className="skip" style={{ width: pct(skippedCount) }} />
      </div>
      <div className="tiny muted" style={{ marginTop: 3 }}>
        {succeededCount}/{totalCount} delivered
        {failedCount > 0 && <span style={{ color: 'var(--danger)' }}> · {failedCount} failed</span>}
        {skippedCount > 0 && <span> · {skippedCount} skipped</span>}
      </div>
    </div>
  );
}
